import { InspectionContext, TireSeason } from '@navio/nsdk';
import { NoddiBasicCard, NoddiCardHeader, NoddiEmptyContent, NoddiInfoField } from 'noddi-ui-web';
import { NavioTireSeasonChip, type NavioTireSeasonChipTranslations } from '../CarWheel/NavioTireSeasonChip';
import { TireDimensionLabel } from '../Tire/TireDimensionLabel';
import { WheelPhotoGallery, type WheelPhoto } from './WheelPhotoGallery';

export interface InspectionCardTranslations {
  title: string;
  dimensions: string;
  frontTires: string;
  rearTires: string;
  inspectionDate: string;
  inspectedBy: string;
  beforeService: string;
  afterService: string;
  standalone: string;
  noPhoto: string;
}

export interface InspectionCardProps {
  season?: TireSeason | null;
  tireDimensions: {
    all?: string;
    front?: string;
    rear?: string;
  };
  wheels: WheelPhoto[];
  inspectionContext?: InspectionContext;
  inspectionDate?: string;
  inspectedBy?: string;
  translations?: InspectionCardTranslations;
  seasonBadgeTranslations?: NavioTireSeasonChipTranslations;
}

const defaultTranslations: InspectionCardTranslations = {
  title: 'Wheel inspection data',
  dimensions: 'Dimensions',
  frontTires: 'Front tires',
  rearTires: 'Rear tires',
  inspectionDate: 'Date of inspection',
  inspectedBy: 'Inspected by',
  beforeService: 'Before service',
  afterService: 'After service',
  standalone: 'Standalone',
  noPhoto: 'No photo yet'
};

export const InspectionCard = ({
  season,
  tireDimensions,
  wheels,
  inspectionContext,
  inspectionDate,
  inspectedBy,
  translations = defaultTranslations,
  seasonBadgeTranslations
}: InspectionCardProps) => {
  const hasTireDimensions = tireDimensions.all || tireDimensions.front || tireDimensions.rear;
  const hasImages = wheels.some((wheel) => wheel.imageUrl);
  if (!hasTireDimensions && !hasImages) {
    return (
      <NoddiEmptyContent
        size='small'
        title='No inspection data'
        message='No inspection photos or tire dimensions are recorded.'
      />
    );
  }

  return (
    <NoddiBasicCard className='flex flex-col gap-2 overflow-hidden p-0'>
      <div className='flex flex-col gap-4'>
        <div className='flex flex-col gap-2 px-5 pt-5'>
          <NoddiCardHeader
            title={translations.title}
            isSmall
            description={getContextLabel(inspectionContext, translations)}
            headerNode={
              <div className='flex flex-row items-center gap-2'>
                <NavioTireSeasonChip translations={seasonBadgeTranslations} season={season} size='sm' />
              </div>
            }
          />

          <div className='flex flex-col gap-2'>
            {hasTireDimensions && (
              <>
                {tireDimensions.all ? (
                  <NoddiInfoField
                    label={translations.dimensions}
                    value={<TireDimensionLabel value={tireDimensions.all} />}
                    textToCopy={tireDimensions.all}
                  />
                ) : (
                  <>
                    {tireDimensions.front && (
                      <NoddiInfoField
                        label={translations.frontTires}
                        value={<TireDimensionLabel value={tireDimensions.front} />}
                        textToCopy={tireDimensions.front}
                      />
                    )}
                    {tireDimensions.rear && (
                      <NoddiInfoField
                        label={translations.rearTires}
                        value={<TireDimensionLabel value={tireDimensions.rear} />}
                        textToCopy={tireDimensions.rear}
                      />
                    )}
                  </>
                )}
              </>
            )}
            {inspectionDate && (
              <NoddiInfoField label={translations.inspectionDate} value={inspectionDate} type='date' />
            )}
            {inspectedBy && <NoddiInfoField label={translations.inspectedBy} value={inspectedBy} />}
          </div>
        </div>

        {hasImages && (
          <WheelPhotoGallery
            wheels={wheels}
            tireDimensions={tireDimensions}
            seasonText={getSeasonText(season, seasonBadgeTranslations)}
            noPhotoLabel={translations.noPhoto}
          />
        )}
      </div>
    </NoddiBasicCard>
  );
};

const getSeasonText = (
  season: InspectionCardProps['season'],
  seasonBadgeTranslations: NavioTireSeasonChipTranslations | undefined
) => {
  if (!season) {
    return undefined;
  }
  if (!seasonBadgeTranslations) {
    return season;
  }
  if (season === TireSeason.SUMMER) {
    return seasonBadgeTranslations.summerTires;
  }
  if (season === TireSeason.WINTER) {
    return seasonBadgeTranslations.winterTires;
  }
  return seasonBadgeTranslations.allSeasonTires;
};

const getContextLabel = (
  inspectionContext: InspectionContext | undefined,
  translations: InspectionCardTranslations
) => {
  if (!inspectionContext) {
    return undefined;
  }

  switch (inspectionContext) {
    case InspectionContext.PRE_JOB:
      return translations.beforeService;
    case InspectionContext.POST_JOB:
      return translations.afterService;
    case InspectionContext.STANDALONE:
      return translations.standalone;
    default:
      return undefined;
  }
};
